import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { AlertTriangle, Download, RefreshCw } from "lucide-react";
import {
  TrendPanel,
  GroupByPanel,
  CustomersPanel,
  ProductsPanel,
} from "./AdminSalesTaxSlice2a";
import { RefundsAdjustmentsPanel } from "./AdminSalesTaxSlice2b";
import { LeadSourcesPanel, OrderStatusPanel, ExportsPanel } from "./AdminSalesTaxSlice2c";

/**
 * Phase 1a · Admin Sales & Tax dashboard skeleton.
 * — 13 top summary cards
 * — Date-range preset selector (Today / Yesterday / Last 7 / Last 30 /
 *   This Month / Last Month / This Year / Last Year / Custom)
 * — Reporting timezone label = America/New_York
 * — Tax Collected drill-down modal
 * — Data Quality panel
 * — Historical backfill preview card
 *
 * NO changes to any other page. Everything is admin-only.
 * NO metric silently zeroes: unavailable metrics show "Data incomplete".
 */

const PRESETS = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["last_7", "Last 7 Days"],
  ["last_30", "Last 30 Days"],
  ["this_month", "This Month"],
  ["last_month", "Last Month"],
  ["this_year", "This Year"],
  ["last_year", "Last Year"],
];

const DOLLAR = (cents) => {
  if (cents == null || Number.isNaN(cents)) return "Data incomplete";
  return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Slice 2b — Refunds is now a REAL metric (append-only ledger). Discounts
// remain unavailable until a discount capture path lands in checkout.
const UNAVAILABLE_METRICS = new Set(["discount_total"]);

const METRIC_CARDS = [
  ["gross_sales",           "Gross Sales",           "cents"],
  ["discount_total",        "Discounts",             "cents"],
  ["net_merchandise_sales", "Net Merchandise Sales", "cents"],
  ["tax_collected",         "Tax Collected",         "cents", "tax-drilldown"],
  ["shipping_charged",      "Shipping Revenue",      "cents"],
  ["customer_total",        "Customer Total",        "cents"],
  ["amount_paid",           "Amount Paid",           "cents"],
  ["net_collected",         "Net Collected",         "cents"],
  ["balance_due",           "Outstanding Balance",   "cents"],
  ["orders",                "Orders",                "count"],
  ["items_sold",            "Items Sold",            "count"],
  ["avg_order_value",       "Avg Order Value",       "cents"],
  ["refund_total",          "Refunds",               "cents"],
];

function pctDelta(cur, prev) {
  if (prev == null || prev === 0) return null;
  return Math.round(((cur - prev) / prev) * 100);
}

function Card({ metricKey, label, kind, cur, prev, drilldownKey, onDrilldown }) {
  const isUnavailable = UNAVAILABLE_METRICS.has(metricKey);
  const shown = isUnavailable
    ? "Data incomplete"
    : (kind === "cents" ? DOLLAR(cur) : String(cur ?? "Data incomplete"));
  const diff = isUnavailable ? null : (cur ?? 0) - (prev ?? 0);
  const pct = isUnavailable ? null : pctDelta(cur, prev);
  return (
    <div
      className={`rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[3px_3px_0_var(--ink)] ${drilldownKey ? "cursor-pointer hover:bg-neutral-50" : ""}`}
      data-testid={`sales-card-${metricKey}`}
      onClick={drilldownKey ? () => onDrilldown(drilldownKey) : undefined}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${isUnavailable ? "text-[var(--smoke)] italic" : ""}`}>{shown}</div>
      {!isUnavailable && prev != null && (
        <div className="text-xs text-[var(--smoke)] mt-1"
             data-testid={`sales-card-${metricKey}-delta`}>
          Prev: {kind === "cents" ? DOLLAR(prev) : prev}
          {pct != null && <span className={`ml-2 ${pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {pct >= 0 ? "▲" : "▼"} {Math.abs(pct)}%
          </span>}
        </div>
      )}
      {isUnavailable && (
        <div className="text-[10px] mt-1 text-yellow-800"
             data-testid={`sales-card-${metricKey}-unavailable`}>
          Refund/discount capture not yet in the order schema
        </div>
      )}
    </div>
  );
}

// ── P0 · Items Sold by Variant (2026-02-16) ─────────────────────────
// Gross merchandise sold, broken down into the White / Blue / Gold /
// Other sleeves the owner asked for. NEVER label these as "net" —
// historical refunds cannot be attributed to a specific sleeve without
// per-line refund data (which only NEW refunds captured after this
// change will carry).
const VARIANT_ORDER = ["white", "blue", "gold", "other"];
const VARIANT_LABEL = { white: "White", blue: "Blue", gold: "Gold", other: "Other" };
const VARIANT_ACCENT = {
  white: "border-neutral-400",
  blue:  "border-blue-600",
  gold:  "border-amber-500",
  other: "border-neutral-300",
};
function ItemsSoldByVariant({ summary }) {
  const bv = summary?.items_sold_by_variant || {};
  const totalQty = Number(summary?.items_sold || 0);
  const totalGross = Number(summary?.gross_sales || 0);
  const qtySum = VARIANT_ORDER.reduce((s, k) => s + Number(bv[k]?.quantity || 0), 0);
  const grossSum = VARIANT_ORDER.reduce((s, k) => s + Number(bv[k]?.gross_cents || 0), 0);
  // Owner rule: reconciliation must hold. Surface any drift immediately
  // rather than silently mislead. (This can only happen from a bug in
  // the backend aggregator; the classifier is exhaustive.)
  const qtyDrift = qtySum - totalQty;
  const grossDrift = grossSum - totalGross;
  return (
    <section
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
      data-testid="items-sold-by-variant"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">Items Sold — by Variant</h2>
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
          Gross merchandise sold · refunds tracked separately
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div
          className="rounded-lg border-2 border-[var(--ink)] p-3 bg-[var(--paper)]"
          data-testid="variant-tile-total"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--smoke)]">Total items sold</div>
          <div className="font-display text-2xl mt-1">{totalQty}</div>
          <div className="text-xs text-[var(--smoke)] mt-1">
            {DOLLAR(totalGross)} <span className="italic">gross merchandise sold</span>
          </div>
        </div>
        {VARIANT_ORDER.map((k) => {
          const q = Number(bv[k]?.quantity || 0);
          const g = Number(bv[k]?.gross_cents || 0);
          return (
            <div
              key={k}
              className={`rounded-lg border-2 ${VARIANT_ACCENT[k]} p-3 bg-white`}
              data-testid={`variant-tile-${k}`}
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--smoke)]">{VARIANT_LABEL[k]}</div>
              <div className="font-display text-2xl mt-1" data-testid={`variant-qty-${k}`}>{q}</div>
              <div className="text-xs text-[var(--smoke)] mt-1" data-testid={`variant-gross-${k}`}>
                {DOLLAR(g)} <span className="italic">gross merchandise sold</span>
              </div>
            </div>
          );
        })}
      </div>
      {(qtyDrift !== 0 || grossDrift !== 0) && (
        <div className="text-[11px] text-red-800" data-testid="variant-recon-warning">
          Reconciliation drift detected — variant totals do not sum to overall totals.
          Δ qty {qtyDrift}, Δ gross {DOLLAR(grossDrift)}.
        </div>
      )}
      <div className="text-[11px] text-[var(--smoke)] italic">
        Variant dollars are GROSS. Refunds are recorded separately in the &ldquo;Refunds&rdquo; tile above and cannot be attributed to a single variant
        for historical adjustments. Line-attributed refunds recorded going forward will surface variant-level net revenue in a future release.
      </div>
    </section>
  );
}



function TaxDrilldownModal({ preset, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/admin/sales/tax-drilldown?preset=${preset}`)
      .then(r => setRows(r.data))
      .catch(e => setErr(formatApiError(e?.response?.data?.detail) || "Failed"));
  }, [preset]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         data-testid="tax-drilldown-modal">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-auto bg-white rounded-2xl border-2 border-[var(--ink)] shadow-[6px_6px_0_var(--ink)] p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl">Tax Collected — drill-down</h3>
          <button onClick={onClose} className="text-xs uppercase font-bold" data-testid="tax-drilldown-close">Close</button>
        </div>
        {err && <div className="mt-2 text-red-700 text-sm">{err}</div>}
        {rows && (
          <>
            <div className="text-xs text-[var(--smoke)] mt-2" data-testid="tax-drilldown-reconcile">
              {rows.row_count} orders · net tax total = {DOLLAR(rows.total_net_tax_cents)}
            </div>
            <table className="w-full mt-3 text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[var(--smoke)]">
                  <th className="pb-1">Order</th>
                  <th className="pb-1">Type</th>
                  <th className="pb-1">Juris.</th>
                  <th className="pb-1 text-right">Taxable</th>
                  <th className="pb-1 text-right">Tax</th>
                </tr>
              </thead>
              <tbody>
                {rows.rows.map((r, i) => (
                  <tr key={i} className="border-t border-neutral-100">
                    <td className="py-1 font-mono text-xs">{r.public_order_ref}</td>
                    <td className="py-1">{r.source_type}</td>
                    <td className="py-1">{r.jurisdiction || "—"}</td>
                    <td className="py-1 text-right">{DOLLAR(r.taxable_amount)}</td>
                    <td className="py-1 text-right">{DOLLAR(r.tax_collected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminSalesTax() {
  const [preset, setPreset] = useState("last_30");
  const [dateBasis, setDateBasis] = useState("payment_date");
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [dq, setDq] = useState(null);
  const [preview, setPreview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [taxReport, setTaxReport] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);

  const loadAll = async () => {
    setLoading(true); setErr(null);
    try {
      const [s, d, p, f, tr] = await Promise.all([
        api.get(`/admin/sales/summary?preset=${preset}&date_basis=${dateBasis}`),
        api.get(`/admin/sales/data-quality-expanded`),
        api.get(`/admin/sales/backfill-preview`),
        api.get(`/admin/sales/status-funnel?preset=${preset}&date_basis=${dateBasis}`),
        api.get(`/admin/sales/tax-report?preset=${preset}&date_basis=${dateBasis}`),
      ]);
      setSummary(s.data); setDq(d.data); setPreview(p.data);
      setFunnel(f.data); setTaxReport(tr.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load Sales & Tax");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [preset, dateBasis]);

  const cur = summary?.current || {};
  const prev = summary?.previous || {};

  const TABS = [
    ["overview", "Overview", true],
    ["tax", "Tax", true],
    ["data_quality", "Data Quality", true],
    ["exports", "Exports", true],
    ["trends", "Trends", true],
    ["payments", "Payments", true],
    ["fulfillment", "Fulfillment", true],
    ["lead_sources", "Lead Sources", true],
    ["products", "Products", true],
    ["order_status", "Order Status", true],
    ["customers", "Customers", true],
    ["refunds", "Refunds & Adj.", true],
  ];

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto" data-testid="admin-sales-tax">
      <SEO title="Sales & Tax · Admin · CollectorUp" />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-3xl" data-testid="sales-tax-title">Sales &amp; Tax</h1>
          <div className="text-xs uppercase text-[var(--smoke)] tracking-widest"
               data-testid="sales-tax-tz">
            Reporting timezone · America/New_York · Preview environment
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <select value={preset} onChange={e => setPreset(e.target.value)}
                  className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm"
                  data-testid="sales-preset">
            {PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={dateBasis} onChange={e => setDateBasis(e.target.value)}
                  className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm"
                  data-testid="sales-date-basis">
            <option value="payment_date">Payment date</option>
            <option value="order_date">Order date</option>
            <option value="completion_date">Completion date</option>
          </select>
          <button onClick={loadAll}
                  className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm inline-flex items-center gap-1"
                  data-testid="sales-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="text-sm">Loading…</div>}
      {err && <div className="p-3 border-2 border-red-500 bg-red-50 rounded-lg text-red-800 text-sm">{err}</div>}

      {/* Phase 1b · Tab bar */}
      <div className="flex gap-1 flex-wrap border-b-2 border-[var(--ink)] pb-1 overflow-x-auto"
           data-testid="sales-tabs">
        {TABS.map(([id, label, live]) => (
          <button key={id}
                  onClick={() => setTab(id)}
                  disabled={!live}
                  data-testid={`sales-tab-${id}`}
                  className={`px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-t-lg whitespace-nowrap ${
                    tab === id ? "bg-[var(--ink)] text-white"
                    : live ? "hover:bg-neutral-100"
                    : "text-neutral-400 cursor-not-allowed"}`}>
            {label}{!live && <span className="ml-1 text-[9px] normal-case">soon</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
               data-testid="sales-metric-grid">
            {METRIC_CARDS.map(([k, l, kind, d]) => (
              <Card key={k} metricKey={k} label={l} kind={kind}
                    cur={cur[k]} prev={prev[k]}
                    drilldownKey={d}
                    onDrilldown={(dk) => setDrilldown(dk)} />
            ))}
          </div>
          {/* P0 · Orders-count clarification (2026-02-16). "Orders" on the
              sales dashboard measures revenue-eligible orders in the
              selected reporting window — it will NOT match the Home
              Dashboard's "All Orders" tile, which counts every order
              document across every status and every time. This is the
              owner-approved explanatory copy. */}
          <div className="text-[11px] text-[var(--smoke)] italic -mt-2"
               data-testid="sales-orders-explainer">
            &ldquo;Orders&rdquo; = revenue-eligible orders in the selected period · excludes unpaid, cancelled and test orders.
            The Home Dashboard &ldquo;All Orders&rdquo; tile counts every order across all statuses and all time — they are not
            expected to match.
          </div>
          {/* P0 · Items Sold — Variant Breakdown (2026-02-16). GROSS
              merchandise sold per variant sleeve. Refund attribution
              across variants is NOT deterministic; historical refunds
              stay in the aggregate Refunds tile. */}
          <ItemsSoldByVariant summary={cur} />
          {funnel && (
            <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4"
                     data-testid="funnel-panel">
              <h2 className="font-display text-lg">Order-Status Funnel</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm mt-2">
                {["pending","collected","cancelled","refunded","unknown"].map(pool => {
                  const p = funnel.by_pool[pool] || {orders: 0, customer_total: 0, amount_paid: 0};
                  return (
                    <div key={pool} className="border-2 border-[var(--ink)] rounded-lg p-2"
                         data-testid={`funnel-pool-${pool}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{pool}</div>
                      <div className="font-mono">{p.orders} orders</div>
                      <div className="text-xs">Customer total: {DOLLAR(p.customer_total)}</div>
                      <div className="text-xs">Amount paid: {DOLLAR(p.amount_paid)}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {preview && (
            <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4"
                     data-testid="backfill-preview-panel">
              <h2 className="font-display text-lg">Historical Ledger Backfill — READ-ONLY preview</h2>
              <div className="text-xs text-[var(--smoke)]">
                No ledger writes. Approval + Phase 1c required before running the real backfill.
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-sm">
                <div><div className="text-[10px] uppercase text-[var(--smoke)]">Orders inspected</div>
                     <div className="font-mono">{preview.orders_inspected}</div></div>
                <div><div className="text-[10px] uppercase text-[var(--smoke)]">Eligible</div>
                     <div className="font-mono">{preview.orders_eligible_for_include_entry}</div></div>
                <div><div className="text-[10px] uppercase text-[var(--smoke)]">Proposed customer total</div>
                     <div className="font-mono">{DOLLAR(preview.proposed_customer_total_cents)}</div></div>
                <div><div className="text-[10px] uppercase text-[var(--smoke)]">Proposed net tax</div>
                     <div className="font-mono">{DOLLAR(preview.proposed_net_tax_cents)}</div></div>
              </div>
            </section>
          )}
        </>
      )}

      {tab === "tax" && taxReport && (
        <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-2"
                 data-testid="tax-report-panel">
          <h2 className="font-display text-lg">Tax Report</h2>
          <div className="text-[10px] text-[var(--smoke)]">{taxReport.note}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[10px] uppercase">Gross taxable</div>
                 <div className="font-mono" data-testid="tax-gross-taxable">{DOLLAR(taxReport.gross_taxable_sales_cents)}</div></div>
            <div><div className="text-[10px] uppercase">Non-taxable</div>
                 <div className="font-mono">{DOLLAR(taxReport.non_taxable_sales_cents)}</div></div>
            <div><div className="text-[10px] uppercase">Net tax</div>
                 <div className="font-mono" data-testid="tax-net">{DOLLAR(taxReport.net_tax_cents)}</div></div>
            <div><div className="text-[10px] uppercase">Taxed / Untaxed</div>
                 <div className="font-mono">{taxReport.taxed_orders} / {taxReport.untaxed_orders}</div></div>
          </div>
          <div className="text-xs">Rates observed: <span className="font-mono">{JSON.stringify(taxReport.tax_rates_used)}</span></div>
          {Object.keys(taxReport.flags).length > 0 && (
            <div className="text-xs">
              <div className="text-[10px] uppercase text-yellow-800">Flags (never auto-repaired)</div>
              <ul className="list-disc list-inside">
                {Object.entries(taxReport.flags).map(([k,v]) => <li key={k}><b>{v}</b> — {k}</li>)}
              </ul>
            </div>
          )}
        </section>
      )}

      {tab === "data_quality" && dq && (
        <section className="rounded-2xl border-2 border-yellow-500 bg-yellow-50 p-4"
                 data-testid="dq-panel">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-yellow-800" />
            <h2 className="font-display text-lg">Data Quality — {dq.orders_with_flags} orders flagged</h2>
          </div>
          <ul className="mt-2 text-xs list-disc list-inside" data-testid="dq-flag-list">
            {Object.entries(dq.flags_by_type).map(([k, v]) => (
              <li key={k}><b>{v}</b> — {k}</li>
            ))}
          </ul>
          <div className="mt-3">
            <div className="text-[10px] uppercase text-yellow-900 tracking-widest">Schema-level gaps</div>
            <ul className="text-xs list-disc list-inside" data-testid="dq-schema-gap-list">
              {dq.schema_gaps.map(g => <li key={g}>{g}</li>)}
            </ul>
          </div>
        </section>
      )}

      {tab === "exports" && (
        <ExportsPanel preset={preset} dateBasis={dateBasis} />
      )}

      {tab === "trends" && (
        <TrendPanel preset={preset} dateBasis={dateBasis} />
      )}

      {tab === "payments" && (
        <GroupByPanel preset={preset} dateBasis={dateBasis}
                      endpoint="payment-methods" drilldownEndpoint="payment-methods-drilldown"
                      labelKey="method" title="Payment Methods" testId="payment-methods-panel" />
      )}

      {tab === "fulfillment" && (
        <GroupByPanel preset={preset} dateBasis={dateBasis}
                      endpoint="fulfillment" drilldownEndpoint="fulfillment-drilldown"
                      labelKey="method" title="Fulfillment Methods" testId="fulfillment-panel" />
      )}

      {tab === "products" && (
        <ProductsPanel preset={preset} dateBasis={dateBasis} />
      )}

      {tab === "customers" && (
        <CustomersPanel preset={preset} dateBasis={dateBasis} />
      )}

      {tab === "refunds" && (
        <RefundsAdjustmentsPanel />
      )}

      {tab === "lead_sources" && (
        <LeadSourcesPanel preset={preset} dateBasis={dateBasis} />
      )}

      {tab === "order_status" && (
        <OrderStatusPanel preset={preset} dateBasis={dateBasis} />
      )}

      {drilldown === "tax-drilldown" && (
        <TaxDrilldownModal preset={preset} onClose={() => setDrilldown(null)} />
      )}
    </div>
  );
}
